import { create } from "zustand"
import api from "../services/api"

const respostaRapidaPaginadaChaveGerar = (cBusca = "", nPagina = 1, nUsuarioId = "", mFilas = []) =>
  `${cBusca}|${nPagina}|${nUsuarioId}|${mFilas.join(",")}`

const respostaRapidaCorrespondeBusca = (cBusca, oRecord) => {
  if (!cBusca) return true
  const cTermo = cBusca.toLowerCase().trim()
  return (oRecord.shortcode || "").toLowerCase().includes(cTermo)
}

export const respostaRapidaUsuarioPodeVer = (oRecord, nUsuarioId, mFilas) => {
  const lEhDoUsuario = oRecord.userId === nUsuarioId
  const lEhGlobal = oRecord.isGlobal === 1
  const nFilaId = oRecord.filaId
  const lTemAcessoFila = !nFilaId || mFilas.includes(nFilaId)
  return lEhDoUsuario || (lEhGlobal && lTemAcessoFila)
}

const respostaRapidaNormalizar = (oRecord) => ({
  ...oRecord,
  queue: oRecord.filaId ? oRecord.queue : null,
})

const respostaRapidaAtualizarNaLista = (mLista, oRecord) => {
  const nIndice = mLista.findIndex((oItem) => oItem.id === oRecord.id)
  const oNormalizado = respostaRapidaNormalizar(oRecord)
  if (nIndice === -1) {
    return [...mLista, oNormalizado]
  }
  const mNova = [...mLista]
  mNova[nIndice] = { ...mNova[nIndice], ...oNormalizado }
  return mNova
}

const respostaRapidaRemoverDaLista = (mLista, nId) => mLista.filter((oItem) => oItem.id !== nId)

export const useRespostaRapidaStore = create((set, get) => ({
  nGeracaoCache: 0,
  mListaSimples: [],
  oPaginadoPorChave: {},
  lPaginadoInvalido: true,
  lListaSimplesInvalida: true,
  oBuscaListaSimplesPendente: null,
  oBuscaPaginadaPendentePorChave: {},

  reiniciar: () =>
    set((oEstado) => ({
      nGeracaoCache: oEstado.nGeracaoCache + 1,
      mListaSimples: [],
      oPaginadoPorChave: {},
      lPaginadoInvalido: true,
      lListaSimplesInvalida: true,
      oBuscaListaSimplesPendente: null,
      oBuscaPaginadaPendentePorChave: {},
    })),

  invalidarTudo: () =>
    set((oEstado) => ({
      nGeracaoCache: oEstado.nGeracaoCache + 1,
      mListaSimples: [],
      oPaginadoPorChave: {},
      lPaginadoInvalido: true,
      lListaSimplesInvalida: true,
      oBuscaListaSimplesPendente: null,
      oBuscaPaginadaPendentePorChave: {},
    })),

  listaSimplesBuscar: async ({ companyId: nCompanyId, userId: nUsuarioId, filas: mFilas = [], force: lForcar = false } = {}) => {
    const oEstado = get()
    if (!lForcar && !oEstado.lListaSimplesInvalida && oEstado.mListaSimples.length > 0) {
      return oEstado.mListaSimples
    }

    if (oEstado.oBuscaListaSimplesPendente) {
      return oEstado.oBuscaListaSimplesPendente
    }

    const nGeracao = oEstado.nGeracaoCache
    const oPromessa = api
      .get("/quick-messages/list", {
        params: { companyId: nCompanyId, userId: nUsuarioId, filas: mFilas },
      })
      .then(({ data }) => {
        if (get().nGeracaoCache !== nGeracao) {
          return []
        }
        const mLista = data || []
        set({ mListaSimples: mLista, lListaSimplesInvalida: false, oBuscaListaSimplesPendente: null })
        return mLista
      })
      .catch((oErro) => {
        if (get().nGeracaoCache === nGeracao) {
          set({ oBuscaListaSimplesPendente: null })
        }
        throw oErro
      })

    set({ oBuscaListaSimplesPendente: oPromessa })
    return oPromessa
  },

  listaSimplesObter: async (oParametros) => get().listaSimplesBuscar(oParametros),

  paginadaBuscar: async ({
    searchParam: cBusca = "",
    pageNumber: nPagina = 1,
    userId: nUsuarioId,
    filas: mFilas = [],
    force: lForcar = false,
  } = {}) => {
    const cChave = respostaRapidaPaginadaChaveGerar(cBusca, nPagina, nUsuarioId, mFilas)
    const oEstado = get()

    if (!lForcar && !oEstado.lPaginadoInvalido && oEstado.oPaginadoPorChave[cChave]) {
      return oEstado.oPaginadoPorChave[cChave]
    }

    if (oEstado.oBuscaPaginadaPendentePorChave[cChave]) {
      return oEstado.oBuscaPaginadaPendentePorChave[cChave]
    }

    const nGeracao = oEstado.nGeracaoCache
    const oPromessa = api
      .get("/quick-messages", {
        params: { searchParam: cBusca, pageNumber: nPagina, userId: nUsuarioId, filas: mFilas },
      })
      .then(({ data }) => {
        if (get().nGeracaoCache !== nGeracao) {
          return { records: [], hasMore: false, count: 0 }
        }
        const oPayload = {
          records: data.records || [],
          hasMore: data.hasMore,
          count: data.count,
        }
        set((oAtual) => ({
          oPaginadoPorChave: { ...oAtual.oPaginadoPorChave, [cChave]: oPayload },
          lPaginadoInvalido: false,
          oBuscaPaginadaPendentePorChave: { ...oAtual.oBuscaPaginadaPendentePorChave, [cChave]: undefined },
        }))
        return oPayload
      })
      .catch((oErro) => {
        if (get().nGeracaoCache === nGeracao) {
          set((oAtual) => ({
            oBuscaPaginadaPendentePorChave: { ...oAtual.oBuscaPaginadaPendentePorChave, [cChave]: undefined },
          }))
        }
        throw oErro
      })

    set((oAtual) => ({
      oBuscaPaginadaPendentePorChave: { ...oAtual.oBuscaPaginadaPendentePorChave, [cChave]: oPromessa },
    }))
    return oPromessa
  },

  paginadaObter: async (oParametros) => get().paginadaBuscar(oParametros),

  eventoSocketAplicar: (oDados, { userId: nUsuarioId, filas: mFilas = [] } = {}) => {
    const { action: cAcao, record: oRecord, id: nId } = oDados || {}

    set((oEstado) => {
      let mListaSimples = oEstado.mListaSimples
      let oPaginadoPorChave = { ...oEstado.oPaginadoPorChave }

      const aplicarNaListaSimples = (oItem, lIncluir) => {
        if (lIncluir) {
          mListaSimples = respostaRapidaAtualizarNaLista(mListaSimples, oItem)
        } else {
          mListaSimples = respostaRapidaRemoverDaLista(mListaSimples, oItem.id)
        }
      }

      if (cAcao === "delete") {
        const nIdNumero = Number(nId)
        mListaSimples = respostaRapidaRemoverDaLista(mListaSimples, nIdNumero)
      } else if (oRecord && (cAcao === "create" || cAcao === "update")) {
        const lPodeVer = respostaRapidaUsuarioPodeVer(oRecord, nUsuarioId, mFilas)
        if (lPodeVer) {
          mListaSimples = respostaRapidaAtualizarNaLista(mListaSimples, oRecord)
        } else {
          mListaSimples = respostaRapidaRemoverDaLista(mListaSimples, oRecord.id)
        }
      }

      Object.keys(oPaginadoPorChave).forEach((cChave) => {
        const oEntrada = oPaginadoPorChave[cChave]
        if (!oEntrada?.records) return
        const [cBusca] = cChave.split("|")
        let mRecords = oEntrada.records

        if (cAcao === "delete") {
          mRecords = respostaRapidaRemoverDaLista(mRecords, Number(nId))
        } else if (oRecord && (cAcao === "create" || cAcao === "update")) {
          const lPodeVer = respostaRapidaUsuarioPodeVer(oRecord, nUsuarioId, mFilas)
          if (lPodeVer && respostaRapidaCorrespondeBusca(cBusca, oRecord)) {
            mRecords = respostaRapidaAtualizarNaLista(mRecords, oRecord)
          } else {
            mRecords = respostaRapidaRemoverDaLista(mRecords, oRecord.id)
          }
        }

        oPaginadoPorChave[cChave] = { ...oEntrada, records: mRecords }
      })

      return {
        mListaSimples,
        oPaginadoPorChave,
        lListaSimplesInvalida: oEstado.lListaSimplesInvalida,
        lPaginadoInvalido: oEstado.lPaginadoInvalido,
      }
    })
  },
}))

export { respostaRapidaPaginadaChaveGerar }
